import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  updateRequestStatus,
  selectClinicSettingsDetail,
  clinicSettingsDetailStatus,
} from '../../../store/reducers/ClinicSettingsDetailSlice';
import { selectSipAccounts } from '../../../store/reducers/SIPAccountSlice';
import { selectCallPools } from '../../../store/reducers/CallPoolSlice';
import { useSchedule } from './schedule';
import { usePhoneMask, useScenarioSettings } from './callSettings';
import { useSpecializationDoctors } from './notificationSettings';
import { useDirtyTracking } from './useDirtyTracking';
import { useClinicSettingsLoading } from './useClinicSettingsLoading';
import { useClinicSettingsSave } from './useClinicSettingsSave';
import { useScenarioCallSchedule } from './useScenarioCallSchedule';
import { useScenarioSettingsRequests } from './scenarioSettingsRequests';

export const useClinicSettingsDetail = () => {
  const { clinicId } = useParams<{ clinicId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const listState = location.state as
    | { search?: string; pageIndex?: number; pageSize?: number }
    | undefined;

  const item = useAppSelector(selectClinicSettingsDetail);
  const status = useAppSelector(clinicSettingsDetailStatus);
  const sipAccounts = useAppSelector(selectSipAccounts);
  const callPools = useAppSelector(selectCallPools);
  const phone = usePhoneMask();
  const schedule = useSchedule();
  const callSchedule = useSchedule();
  const scenario = useScenarioSettings();
  const doctors = useSpecializationDoctors();
  const dirty = useDirtyTracking();
  const requests = useScenarioSettingsRequests(clinicId);
  const [openPanels, setOpenPanels] = useState<string[]>([]);
  const callScheduleIdRef = useRef<string | null>(null);
  const domain = {
    phone,
    schedule,
    callSchedule,
    scenario,
    doctors,
    dirty,
    callScheduleIdRef,
    requests,
  };
  const { callScheduleLoading, loadCallSchedule, handleScenarioTypeChange } =
    useScenarioCallSchedule(clinicId, domain);
  const {
    sipAccountId,
    setSipAccountId,
    callPoolId,
    setCallPoolId,
    scheduleLoading,
  } = useClinicSettingsLoading(clinicId, item, { ...domain, loadCallSchedule });

  const currentSnapshot = useMemo(
    () => ({
      operatorPhone: phone.operatorPhone,
      sipAccountId,
      callPoolId,
      schedule: schedule.schedule.map((d) => ({ ...d })),
      holidays: [...schedule.holidays],
      callSchedule: callSchedule.schedule.map((d) => ({ ...d })),
      callHolidays: [...callSchedule.holidays],
      callsMaxTries: scenario.callsMaxTries,
      callsIntervalMinutes: scenario.callsIntervalMinutes,
      callsDisable: scenario.callsDisable,
      excludedSpecializationIds: [...doctors.excludedSpecializationIds],
      excludedDoctorIds: [...doctors.excludedDoctorIds],
    }),
    [
      phone.operatorPhone,
      sipAccountId,
      callPoolId,
      schedule.schedule,
      schedule.holidays,
      callSchedule.schedule,
      callSchedule.holidays,
      scenario.callsMaxTries,
      scenario.callsIntervalMinutes,
      scenario.callsDisable,
      doctors.excludedSpecializationIds,
      doctors.excludedDoctorIds,
    ],
  );

  const isDirty = useMemo(() => {
    const clinicOrNotifDirty = dirty.computeIsDirty(currentSnapshot);
    if (clinicOrNotifDirty) return true;

    // Проверяем все сценарии (включая неактивные) через серверные снимки
    const dirtyScenarios = scenario.getDirtyScenarios(
      callScheduleIdRef.current,
      callSchedule.schedule,
      callSchedule.holidays,
    );
    return dirtyScenarios.length > 0;
  }, [
    dirty,
    currentSnapshot,
    scenario.getDirtyScenarios,
    callSchedule.schedule,
    callSchedule.holidays,
  ]);

  const { saving, handleSave } = useClinicSettingsSave(
    clinicId,
    currentSnapshot,
    domain,
  );

  const handleBack = useCallback(
    () => navigate('/settings/calls', { state: listState ?? {} }),
    [navigate, listState],
  );

  const handleDismissNotification = useCallback(
    () => dispatch(updateRequestStatus()),
    [dispatch],
  );

  return {
    item,
    status,
    clinicId,

    phoneDisplay: phone.phoneDisplay,
    handlePhoneChange: phone.handlePhoneChange,

    sipAccountId,
    setSipAccountId,
    sipAccounts,

    callPoolId,
    setCallPoolId,
    callPools,

    schedule: schedule.schedule,
    handleScheduleChange: schedule.handleScheduleChange,
    handleToggleDay: schedule.handleToggleDay,
    holidays: schedule.holidays,
    handleAddHoliday: schedule.handleAddHoliday,
    handleRemoveHoliday: schedule.handleRemoveHoliday,
    scheduleLoading,

    callScheduleData: callSchedule.schedule,
    handleCallScheduleChange: callSchedule.handleScheduleChange,
    handleCallToggleDay: callSchedule.handleToggleDay,
    callHolidays: callSchedule.holidays,
    handleCallAddHoliday: callSchedule.handleAddHoliday,
    handleCallRemoveHoliday: callSchedule.handleRemoveHoliday,
    callScheduleLoading,

    openPanels,
    setOpenPanels,
    saving,
    isDirty,
    handleBack,
    handleSave,
    handleDismissNotification,

    scenarioType: scenario.scenarioType,
    setScenarioType: handleScenarioTypeChange,
    callsMaxTries: scenario.callsMaxTries,
    setCallsMaxTries: scenario.setCallsMaxTries,
    callsIntervalMinutes: scenario.callsIntervalMinutes,
    setCallsIntervalMinutes: scenario.setCallsIntervalMinutes,
    callsDisable: scenario.callsDisable,
    setCallsDisable: scenario.setCallsDisable,

    excludedSpecializationIds: doctors.excludedSpecializationIds,
    excludedDoctorIds: doctors.excludedDoctorIds,
    handleToggleSpecialization: doctors.handleToggleSpecialization,
    handleToggleDoctor: doctors.handleToggleDoctor,
    doctorSearch: doctors.doctorSearch,
    setDoctorSearch: doctors.setDoctorSearch,
    specFilter: doctors.specFilter,
    setSpecFilter: doctors.setSpecFilter,
    clinicSpecializations: doctors.specializations,
    filteredDoctors: doctors.filteredDoctors,
  };
};
